import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { EMPTY, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ErrorModalService } from '../../modal/error.modal.service';
import { CreditorService } from '../creditor.service';
import { CreditorCaseDetailed } from '../../interfaces/creditorCaseDetailed.interface';
import { CreditorClaimProofOfDebtSummary } from '../../interfaces/creditorClaimProofOfDebtSummary.interface';
import { CreditorCaseSummary } from '../../interfaces/creditorCaseSummary.interface';
import { AllowedProofOfDebtAlternativeType } from '../../enum/allowedproofofdebtalternativetype.enum';
import { formatClaimType } from '../../_helpers/enum-label.helper';
import { SessionService } from '../../session/session.service';

@Component({
    selector: 'app-creditor-case',
    templateUrl: './creditor-case.component.html',
    styleUrls: ['./creditor-case.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorCaseComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public isLoading = true;
  public detailed: CreditorCaseDetailed | null = null;
  public caseTitle = '...';
  public caseId = '';
  public originalCreditorIdForClaimOrProofOfDebt = '';

  public anmalningar: CreditorClaimProofOfDebtSummary[] = [];
  public bevakningar: CreditorClaimProofOfDebtSummary[] = [];
  public searchTerm = '';
  public sortDirection: 'asc' | 'desc' = 'desc';
  public newlySubmittedId: string | null = null;
  public showSubmitSuccess = false;
  public downloadingReceiptId: string | null = null;
  public isProofOfDebtInfoExpanded = false;

  private pendingSubmitSuccess = false;
  private pendingSubmittedClaimId: string | null = null;
  private pendingLastSubmittedSaveFailed = false;
  private pendingFilesNotAttached = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private creditorService: CreditorService,
    private spinner: NgxSpinnerService,
    private errorModalService: ErrorModalService,
    private translate: TranslateService,
    private decimalPipe: DecimalPipe,
    private sessionService: SessionService,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  public get allowPreRequests(): boolean {
    return this.detailed?.allowedProofOfDebtsAlternatives === AllowedProofOfDebtAlternativeType.PreRequest;
  }

  public get allowRequests(): boolean {
    return this.detailed?.allowedProofOfDebtsAlternatives === AllowedProofOfDebtAlternativeType.ProofOfDebt;
  }

  public get allowLateRequests(): boolean {
    return this.detailed?.allowedProofOfDebtsAlternatives === AllowedProofOfDebtAlternativeType.LateProofOfDebt;
  }

  public get isPreRequestStage(): boolean {
    return this.allowPreRequests && !this.allowRequests && !this.allowLateRequests;
  }

  public get isProofOfDebtStage(): boolean {
    return this.allowRequests || this.allowLateRequests;
  }

  public get hasProofOfDebtDates(): boolean {
    return !!(this.detailed?.proofOfDebtStarted || this.detailed?.proofOfDebtDeadline || this.detailed?.remarksDeadline);
  }

  public get hasPreRequests(): boolean {
    return (this.anmalningar?.length ?? 0) > 0;
  }

  public get banner(): { variant: 'info' | 'success' | 'warning' | 'danger'; title: string; text: string } | null {
    if (this.isLoading || !this.detailed) {
      return null;
    }

    if (this.allowPreRequests) {
      return {
        variant: 'info',
        title: 'banner.pre-request-title',
        text: 'banner.pre-request-text'
      };
    }

    if ((this.allowRequests || this.allowLateRequests) && this.anmalningar.length > 0) {
      return {
        variant: 'success',
        title: this.allowLateRequests ? 'banner.late-proof-of-debt-title' : 'banner.proof-of-debt-title',
        text: this.allowLateRequests ? 'banner.late-proof-of-debt-with-claims-text' : 'banner.proof-of-debt-with-claims-text'
      };
    }

    if (this.allowRequests || this.allowLateRequests) {
      return {
        variant: 'success',
        title: this.allowLateRequests ? 'banner.late-proof-of-debt-title' : 'banner.proof-of-debt-title',
        text: this.allowLateRequests ? 'banner.late-proof-of-debt-text' : 'banner.proof-of-debt-text'
      };
    }

    return null;
  }

  ngOnInit(): void {
    const caseName = this.route.snapshot.queryParamMap.get('name');
    if (caseName) {
      this.caseTitle = caseName;
    }

    const navState = this.router.getCurrentNavigation()?.extras?.state ?? (isPlatformBrowser(this.platformId) ? history.state : null);
    if (navState?.['submitted']) {
      this.pendingSubmitSuccess = true;
      this.pendingSubmittedClaimId = navState['submittedClaimId'] ?? null;
      this.pendingLastSubmittedSaveFailed = navState['lastSubmittedSaveFailed'] === true;
      this.pendingFilesNotAttached = navState['filesNotAttached'] === true;
    }

    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap(params => {
          const caseId = params.get('id') ?? '';
          this.caseId = caseId;

          if (!caseId) {
            this.isLoading = false;
            this.errorModalService.open(this.translate.instant('error.title'), this.translate.instant('error.missing-case-id'));
            return EMPTY;
          }

          this.sessionService.setSelectedCaseId(caseId);
          this.isLoading = true;
          this.spinner.show();
          return this.creditorService.getDetailedCreditorCase(caseId);
        })
      )
      .subscribe({
        next: (creditorCase: CreditorCaseDetailed) => {
          this.applyCreditorCase(creditorCase);
          this.isLoading = false;
          this.spinner.hide();
        },
        error: () => {
          this.isLoading = false;
          this.spinner.hide();
          this.errorModalService.open(this.translate.instant('error.load-error-title'), this.translate.instant('error.could-not-fetch-case'));
          this.navigateBack();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isSearchActive(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  public get filteredAnmalningar(): CreditorClaimProofOfDebtSummary[] {
    return this.sortByDate(this.filterList(this.anmalningar), this.sortDirection);
  }

  public get filteredBevakningar(): CreditorClaimProofOfDebtSummary[] {
    return this.sortByDate(this.filterList(this.bevakningar), this.sortDirection);
  }

  private filterList(list: CreditorClaimProofOfDebtSummary[]): CreditorClaimProofOfDebtSummary[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return list;
    const normalizedTerm = term.replace(/\s/g, '');
    return list.filter(item => {
      const formatted = (this.decimalPipe.transform(item.amount, '1.0-0') ?? '') + ' kr';
      const normalizedFormatted = formatted.replace(/\s/g, '').toLowerCase();
      return (
        formatClaimType(item.claimType).toLowerCase().includes(term) ||
        formatted.toLowerCase().includes(term) ||
        normalizedFormatted.includes(normalizedTerm)
      );
    });
  }

  public clearSearch(): void {
    this.searchTerm = '';
  }

  public toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
  }

  public toggleProofOfDebtInfo(): void {
    this.isProofOfDebtInfoExpanded = !this.isProofOfDebtInfoExpanded;
  }

  public trackById(index: number, claim: CreditorClaimProofOfDebtSummary): string {
    return claim.id ?? `fallback-${index}`;
  }

  private sortByDate(list: CreditorClaimProofOfDebtSummary[], direction: 'asc' | 'desc'): CreditorClaimProofOfDebtSummary[] {
    return [...list].sort((a, b) => {
      if (!a.submittedDate && !b.submittedDate) return 0;
      if (!a.submittedDate) return 1;
      if (!b.submittedDate) return -1;
      const diff = new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime();
      return direction === 'desc' ? diff : -diff;
    });
  }

  private splitClaims(detailed: CreditorCaseDetailed): void {
    const list = detailed.usersProofOfDebtSummaries || [];

    this.anmalningar = list.filter(x => x.isPreRequest);
    this.bevakningar = list.filter(x => !x.isPreRequest);
  }

  private applyCreditorCase(detailed: CreditorCaseDetailed): void {
    this.detailed = detailed;
    this.caseTitle = detailed.caseName || this.caseTitle;
    this.splitClaims(detailed);
    this.sessionService.setHasCreditorClaims(this.anmalningar.length > 0 || this.bevakningar.length > 0);

    if (this.pendingSubmitSuccess) {
      this.pendingSubmitSuccess = false;
      this.newlySubmittedId = this.pendingSubmittedClaimId;
      this.showSubmitSuccess = true;
      setTimeout(() => { this.showSubmitSuccess = false; }, 5000);
      setTimeout(() => { this.newlySubmittedId = null; }, 8000);
      if (this.newlySubmittedId && isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          const element = document.querySelector('.row-item--new') as HTMLElement | null;
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      }
    }

    if (this.pendingLastSubmittedSaveFailed) {
      this.pendingLastSubmittedSaveFailed = false;
      this.errorModalService.open(
        this.translate.instant('modal.unknown-error'),
        this.translate.instant('error.could-not-save-last-submitted')
      );
    }

    if (this.pendingFilesNotAttached) {
      this.pendingFilesNotAttached = false;

      if (isPlatformBrowser(this.platformId)) {
        const { filesNotAttached, ...remainingState } = history.state ?? {};
        history.replaceState(remainingState, '');
      }

      this.errorModalService.open(
        this.translate.instant('modal.files-not-attached-title'),
        this.translate.instant('error.files-not-attached')
      );
    }
  }

  private buildCaseSummary(): CreditorCaseSummary | null {
    if (!this.detailed) {
      return null;
    }

    return {
      caseId: this.detailed.caseId,
      caseName: this.detailed.caseName,
      caseOrgNr: this.detailed.caseOrgNr,
      caseCourtName: this.detailed.caseCourtName,
      caseNumber: this.detailed.caseNumber,
      caseDecisionDate: this.detailed.caseDecisionDate,
      trusteeName: this.detailed.trusteeName,
    };
  }

  public hasOnlyOneList(): boolean {
    const a = ((this.anmalningar?.length ?? 0) > 0) || this.allowPreRequests;
    const b = ((this.bevakningar?.length ?? 0) > 0) || (this.allowRequests || this.allowLateRequests);
    return (a && !b) || (!a && b);
  }

  public createPreRequest(): void {
    this.caseId = this.route.snapshot.paramMap.get('id');
    if (!this.caseId) {
      return;
    }

    this.router.navigate(
      ['/borgenar/arende', this.caseId, 'anmalan', 'ny'],
      {
        state: {
          caseSummary: this.buildCaseSummary()
        }
      }
    );
  }

  public createProofOfDebt(): void {
    this.caseId = this.route.snapshot.paramMap.get('id');
    if (!this.caseId) {
      return;
    }

    if (this.detailed.allowedProofOfDebtsAlternatives === AllowedProofOfDebtAlternativeType.LateProofOfDebt) {
      this.router.navigate(
        ['/borgenar/arende', this.caseId, 'efterbevakning', 'ny'],
        {
          state: {
            caseSummary: this.buildCaseSummary()
          }
        }
      );
    }
    else {
      this.router.navigate(
        ['/borgenar/arende', this.caseId, 'bevakning', 'ny'],
        {
          state: {
            caseSummary: this.buildCaseSummary()
          }
        }
      );
    }
  }

  public viewClaim(claim: CreditorClaimProofOfDebtSummary): void {
    this.caseId = this.route.snapshot.paramMap.get('id');
    if (!this.caseId) {
      return;
    }

    this.router.navigate(['/borgenar/arende', this.caseId, 'anmalan', claim.id], {
      state: { creditorId: claim.creditorId, caseSummary: this.buildCaseSummary() }
    });
  }

  public createProofOfDebtFromClaim(claim: CreditorClaimProofOfDebtSummary): void {
    this.caseId = this.route.snapshot.paramMap.get('id');
    if (!this.caseId || !this.detailed) {
      return;
    }
    if (this.detailed.allowedProofOfDebtsAlternatives === AllowedProofOfDebtAlternativeType.LateProofOfDebt) {
      this.router.navigate(
        ['/borgenar/arende', this.caseId, 'efterbevakning', 'ny', claim.id],
        {
          state: { caseSummary: this.buildCaseSummary(), creditorId: claim.creditorId }
        }
      );
    }
    else {
      this.router.navigate(
        ['/borgenar/arende', this.caseId, 'bevakning', 'ny', claim.id],
        {
          state: { caseSummary: this.buildCaseSummary(), creditorId: claim.creditorId }
        }
      );
    }
  }

  public viewProofOfDebt(claim: CreditorClaimProofOfDebtSummary): void {
    this.caseId = this.route.snapshot.paramMap.get('id');
    if (!this.caseId) {
      return;
    }

    this.router.navigate(
      ['/borgenar/arende', this.caseId, 'bevakning', claim.id],
      {
        state: { creditorId: claim.creditorId, caseSummary: this.buildCaseSummary() }
      }
    );
  }

  public navigateBack(): void {
    this.router.navigate(['/borgenar/start']);
  }

  public downloadReceipt(claim: CreditorClaimProofOfDebtSummary, event: MouseEvent): void {
    event.stopPropagation();

    if (!claim.id || !claim.creditorId || this.downloadingReceiptId) {
      return;
    }

    this.downloadingReceiptId = claim.id;
    const language = this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'se';

    this.creditorService.getProofOfDebtReceipt(this.caseId, claim.creditorId, claim.id, language, claim.isPreRequest, this.detailed?.caseName, claim.submittedDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ blob, filename }) => {
          this.triggerBrowserDownload(blob, filename);
          this.downloadingReceiptId = null;
        },
        error: () => {
          this.downloadingReceiptId = null;
          this.errorModalService.open(
            this.translate.instant('modal.unknown-error'),
            this.translate.instant('error.download-receipt-body')
          );
        }
      });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = objectUrl;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(objectUrl);
  }
}
